import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import movilidadVideo from "../../static/media/Training/movilidad.mp4";
import mejorandoVideo from "../../static/media/Training/mejorando.mp4";
import espaldaVideo from "../../static/media/Training/espalda.mp4";
import descansarVideo from "../../static/media/Training/descansar.mp4";

const MotionTypography = motion(Typography);
const MotionBox = motion(Box);

const COLORS = {
  bg: "#ffffff",
  text: "#111111",
  muted: "rgba(17,17,17,0.72)",
  line: "rgba(17,17,17,0.08)",
  red: "#e50914",
};

export default function TrainingContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openVideo, setOpenVideo] = useState(null);

  const topics = useMemo(
    () => [
      {
        id: "movilidad",
        eyebrow: "Base del movimiento",
        title: "Movilidad y por qué mejora tu entrenamiento",
        description:
          "La movilidad ayuda a que tu cuerpo se mueva mejor, con más control y menos limitaciones. Incluirla dentro de tu rutina puede mejorar la técnica, preparar mejor tus articulaciones y hacer que entrenes con más seguridad desde el inicio.",
        bullets: [
          "Mejora la calidad del movimiento",
          "Ayuda a preparar el cuerpo antes de entrenar",
          "Favorece una mejor técnica en distintos ejercicios",
        ],
        video: movilidadVideo,
        cta: "Ver guía",
      },
      {
        id: "mejorando",
        eyebrow: "Progreso real",
        title: "Cómo saber si estás mejorando",
        description:
          "Mejorar no siempre significa mover más peso de un día a otro. También se nota en el control del ejercicio, en la postura, en la energía y en la constancia. Llevar registro de series, pesos y repeticiones te permite entender mejor tu avance y entrenar con más intención.",
        bullets: [
          "Registra series, pesos y repeticiones",
          "Observa cambios en postura, energía y rendimiento",
        ],
        video: mejorandoVideo,
        cta: "Ver guía",
      },
      {
        id: "espalda",
        eyebrow: "Trabajo específico",
        title: "Cómo entrenar espalda de forma más efectiva",
        description:
          "Entrenar espalda no es solo jalar peso. La técnica, el control y la elección de ejercicios marcan la diferencia. Una buena rutina de espalda puede ayudarte a desarrollar mejor la musculatura, mejorar la postura y sentir más estabilidad en otros movimientos del gimnasio.",
        bullets: [
          "Mejora la activación y el control del movimiento",
          "Ayuda a trabajar mejor la postura",
          "Aporta ideas de rutina, series y enfoque",
        ],
        video: espaldaVideo,
        cta: "Ver guía",
      },
      {
        id: "descansar",
        eyebrow: "Recuperación",
        title: "Por qué el descanso también forma parte del progreso",
        description:
          "Descansar bien no es perder el ritmo, es darle al cuerpo el tiempo que necesita para recuperarse. El descanso influye en la energía, en la adaptación al entrenamiento y en el desarrollo de masa muscular, por eso es una parte clave de cualquier proceso serio en el gimnasio.",
        bullets: [
          "Influye en la energía y el rendimiento",
          "Forma parte del desarrollo y la constancia",
        ],
        video: descansarVideo,
        cta: "Ver guía",
      },
    ],
    []
  );

  const currentVideo = topics.find((item) => item.id === openVideo);

  return (
    <>
      <Box
        component="main"
        sx={{
          bgcolor: COLORS.bg,
          color: COLORS.text,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            width: { xs: "90%", md: "82%" },
            px: 0,
            py: { xs: 3, md: 5 },
          }}
        >
          <Box
            sx={{
              borderRadius: { xs: 4, md: 6 },
              overflow: "hidden",
              border: `1px solid ${COLORS.line}`,
              background:
                "linear-gradient(135deg, rgba(17,17,17,1) 0%, rgba(24,24,28,1) 42%, rgba(229,9,20,0.96) 180%)",
              boxShadow: "0 26px 80px rgba(0,0,0,0.12)",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(700px 320px at 12% 10%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 55%), radial-gradient(500px 260px at 88% 18%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 60%)",
                pointerEvents: "none",
              }}
            />

            <Stack
              spacing={2}
              sx={{
                position: "relative",
                p: { xs: 2.2, md: 4.2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 13, md: 14 },
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.78)",
                }}
              >
                Entrena mejor
              </Typography>

              <MotionTypography
                component="h1"
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                sx={{
                  maxWidth: 920,
                  fontSize: {
                    xs: "clamp(34px, 10vw, 48px)",
                    md: "clamp(48px, 5vw, 72px)",
                  },
                  lineHeight: { xs: 0.98, md: 0.94 },
                  letterSpacing: { xs: -1.6, md: -2.6 },
                  fontWeight: 750,
                  color: "#ffffff",
                }}
              >
                Guías claras para entrenar mejor, recuperarte bien y progresar
                con más intención
              </MotionTypography>

              <Typography
                sx={{
                  maxWidth: 920,
                  fontSize: { xs: 15, md: 18 },
                  lineHeight: 1.75,
                  color: "rgba(255,255,255,0.82)",
                  fontWeight: 550,
                }}
              >
                Contenido claro y práctico para entender mejor aspectos clave
                del entrenamiento, mejorar tu técnica, progresar con más
                criterio y sacarle mayor provecho a cada sesión en NovaXFit.
              </Typography>
            </Stack>
          </Box>

          <Stack
            spacing={1.2}
            sx={{
              mt: { xs: 3, md: 4 },
              mb: { xs: 2.4, md: 3.2 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 26, md: 40 },
                lineHeight: 0.98,
                letterSpacing: -1.2,
                fontWeight: 800,
                color: "#111111",
              }}
            >
              Lo que debes saber
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                xl: "repeat(2, minmax(0, 1fr))",
              },
              gap: { xs: 2.2, md: 3 },
            }}
          >
            {topics.map((topic) => (
              <Box
                key={topic.id}
                sx={{
                  borderRadius: { xs: 4, md: 5 },
                  overflow: "hidden",
                  border: `1px solid ${COLORS.line}`,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
                  bgcolor: "#ffffff",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 2.6 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 12.5, md: 13.5 },
                        fontWeight: 800,
                        letterSpacing: 1.1,
                        textTransform: "uppercase",
                        color: COLORS.red,
                      }}
                    >
                      {topic.eyebrow}
                    </Typography>

                    <Typography
                      component="h2"
                      sx={{
                        mt: 0.8,
                        fontSize: { xs: 25, md: 34 },
                        lineHeight: 0.98,
                        letterSpacing: -1,
                        fontWeight: 800,
                        color: "#111111",
                        maxWidth: 560,
                      }}
                    >
                      {topic.title}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1.1,
                        fontSize: { xs: 14.5, md: 15.8 },
                        lineHeight: 1.75,
                        color: COLORS.muted,
                        fontWeight: 600,
                        maxWidth: 620,
                      }}
                    >
                      {topic.description}
                    </Typography>

                    <Stack spacing={0.9} sx={{ mt: 1.8 }}>
                      {topic.bullets.map((bullet) => (
                        <Box
                          key={bullet}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: COLORS.red,
                              flexShrink: 0,
                              mt: "8px",
                              boxShadow: "0 0 0 5px rgba(229,9,20,0.10)",
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: 14.3, md: 15.2 },
                              lineHeight: 1.65,
                              color: "#111111",
                              fontWeight: 650,
                            }}
                          >
                            {bullet}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    <Button
                      variant="contained"
                      startIcon={<PlayArrowRoundedIcon />}
                      onClick={() => setOpenVideo(topic.id)}
                      sx={{
                        mt: 2,
                        alignSelf: "flex-start",
                        px: 2.6,
                        py: 1.15,
                        borderRadius: 999,
                        textTransform: "none",
                        fontSize: 15.5,
                        fontWeight: 800,
                        bgcolor: "#111111",
                        color: "#ffffff",
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: COLORS.red,
                          boxShadow: "none",
                        },
                      }}
                    >
                      {topic.cta}
                    </Button>
                  </Box>

                  {!isMobile && (
                    <Box
                      sx={{
                        p: { xs: 1.5, md: 1.8 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: {
                          xs: "#ffffff",
                          md: "rgba(17,17,17,0.02)",
                        },
                      }}
                    >
                      <MotionBox
                        initial={{ opacity: 0, y: 28, scale: 0.97 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.75, ease: "easeOut" }}
                        onClick={() => setOpenVideo(topic.id)}
                        sx={{
                          width: { xs: "100%", sm: 290, md: "100%" },
                          maxWidth: 320,
                          borderRadius: 4,
                          overflow: "hidden",
                          position: "relative",
                          cursor: "pointer",
                          bgcolor: "#0f0f10",
                          boxShadow: "0 22px 50px rgba(0,0,0,0.18)",
                          border: "1px solid rgba(17,17,17,0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            aspectRatio: "9 / 16",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            component="video"
                            src={topic.video}
                            muted
                            playsInline
                            preload="metadata"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />

                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              background:
                                "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.16) 42%, rgba(0,0,0,0.58) 100%)",
                            }}
                          />

                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 74,
                                height: 74,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.18)",
                                backdropFilter: "blur(12px)",
                                border: "1px solid rgba(255,255,255,0.20)",
                              }}
                            >
                              <PlayArrowRoundedIcon
                                sx={{
                                  fontSize: 42,
                                  color: "#ffffff",
                                  ml: "4px",
                                }}
                              />
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              position: "absolute",
                              left: 14,
                              right: 14,
                              bottom: 14,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 17,
                                lineHeight: 1.05,
                                letterSpacing: -0.4,
                                fontWeight: 800,
                                color: "#ffffff",
                                textShadow: "0 8px 24px rgba(0,0,0,0.50)",
                              }}
                            >
                              {topic.title}
                            </Typography>

                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.14)",
                                color: "#ffffff",
                                flexShrink: 0,
                              }}
                            >
                              <OpenInFullRoundedIcon sx={{ fontSize: 18 }} />
                            </Box>
                          </Box>
                        </Box>
                      </MotionBox>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Dialog
        open={Boolean(openVideo)}
        onClose={() => setOpenVideo(null)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "#0b0b0d",
            overflow: "hidden",
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            bgcolor: "#0b0b0d",
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <IconButton
            onClick={() => setOpenVideo(null)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 3,
              width: 42,
              height: 42,
              bgcolor: "rgba(0,0,0,0.46)",
              color: "#ffffff",
              backdropFilter: "blur(10px)",
              "&:hover": {
                bgcolor: "rgba(229,9,20,0.88)",
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: "#000000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 0, md: 2 },
            }}
          >
            {currentVideo && (
              <Box
                component="video"
                src={currentVideo.video}
                controls
                autoPlay
                playsInline
                preload="auto"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  bgcolor: "#000000",
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}